import { useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { Email, CheckCircle } from "@mui/icons-material";
import { useForm, SubmitHandler } from "react-hook-form";
import { useRequestEmailVerificationMutation } from "features/auth/auth";
import AppDialog from "./common/AppDialog";

type SetEmailInputs = {
  email: string;
};

const SESSION_KEY = "setEmailPromptDismissed";

export function wasSetEmailDismissed(): boolean {
  return sessionStorage.getItem(SESSION_KEY) === "1";
}

export function setSetEmailDismissed(): void {
  sessionStorage.setItem(SESSION_KEY, "1");
}

export function clearSetEmailDismissed(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

interface SetEmailModalProps {
  open: boolean;
  onClose: () => void;
}

export default function SetEmailModal({ open, onClose }: SetEmailModalProps) {
  const [requestVerification, { isLoading }] =
    useRequestEmailVerificationMutation();
  const [serverError, setServerError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SetEmailInputs>();

  const handleClose = () => {
    setSetEmailDismissed();
    setServerError(null);
    setEmailSent(false);
    setSubmittedEmail("");
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<SetEmailInputs> = async (data) => {
    setServerError(null);

    try {
      await requestVerification({ email: data.email }).unwrap();
      setSubmittedEmail(data.email);
      setEmailSent(true);
    } catch (error: any) {
      if (error?.data?.status === "error" && error?.data?.error?.email) {
        setServerError(error.data.error.email[0] || error.data.error.email);
      } else {
        setServerError(error?.data?.message || "Something went wrong");
      }
    }
  };

  if (emailSent) {
    return (
      <AppDialog
        open={open}
        onClose={handleClose}
        title="Check Your Email"
        actions={
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Close
          </Button>
        }
      >
        <Box display="flex" flexDirection="column" alignItems="center">
          <CheckCircle sx={{ fontSize: 48, color: "#4caf50", mb: 2 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            We sent a verification link to <strong>{submittedEmail}</strong>.
            Click the link in the email to verify your address.
          </Typography>
        </Box>
      </AppDialog>
    );
  }

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Add Your Email"
      actions={
        <>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            Skip for Now
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {isLoading ? "Adding…" : "Add Email"}
          </Button>
        </>
      }
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ width: "100%" }}
      >
        <TextField
          fullWidth
          label="Email Address"
          id="email"
          type="email"
          autoComplete="email"
          autoFocus
          error={Boolean(errors.email) || Boolean(serverError)}
          helperText={errors.email?.message || serverError || ""}
          InputProps={{
            startAdornment: <Email sx={{ color: "gray", mr: 1 }} />,
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          })}
        />
      </Box>
    </AppDialog>
  );
}
